import type { PlpRoot } from '@core/Entities/Search/PlpRoot.entity';

import { SLUG_PARAM_KEYS, slugParamMapping } from '../../Constants/Plp';
import type { Filter } from '../../Entities/Search/SearchFilters/Filter.entity';
import { getRangeFilterPartnerId } from '../../Services/Search/Utils/GetRangeFilterPartnerId';
import { getSlugParamFromFilter } from '../../Services/Search/Utils/GetSlugParamFromFilter';
import { getSlugParamValueFromFilter } from '../SearchFilters/GetSlugParamValueFromFilter';
import { createQueryStrFromFilters } from './CreateQueryStrFromFilters';
import { getRangeVal } from './GetRangeVal';
import { hasPriceRangeForPath } from './HasPriceRangeForPath';
import { hasSpaceInPath } from './HasSpaceInPath';

type FilterPlacement = 'path' | 'query' | undefined;

const considerPriceForPath = (baseName: string, root: PlpRoot) =>
  (root === 'lease-deals' && baseName === 'monthly-price') ||
  (root !== 'lease-deals' && baseName === 'price');

function createPathFromFilters(paramsArray: Filter[], root: PlpRoot = 'used') {
  const rangeFltrs: Filter[] = [];
  const forPath: Filter[] = [];
  const forQuery: Filter[] = [];
  let makePosition: FilterPlacement;
  let modelPosition: FilterPlacement;
  let variantPosition: FilterPlacement;
  const pagePath = `/${root}`;
  const newPath = ['/cars', pagePath];

  // split into Path or Query
  paramsArray.forEach(filter => {
    const { id, value } = filter;
    const isSpaceInPath = hasSpaceInPath(
      { addition: filter, pathList: forPath, allFilters: paramsArray },
      root,
    );
    //multi selects with more than one value
    if (Array.isArray(value) && value?.length > 1) {
      forQuery.push(filter);
      if (id === 'make') makePosition = 'query';
      if (id === 'model') modelPosition = 'query';
      if (id === 'variant-type') variantPosition = 'query';
    }
    //range filter are set aside and processed after
    else if (id.includes('__gte') || id.includes('__lte')) {
      rangeFltrs.push(filter);
    }
    //don't want page-1 in the url
    else if (id === 'page' && (value === '1' || value === (1 as any) || !value)) {
      return;
    }
    //marked as a slug param and path isn't at limit
    else if (getSlugParamFromFilter(id, root) && isSpaceInPath) {
      forPath.push(filter);
      if (id === 'make') makePosition = 'path';
      if (id === 'model') modelPosition = 'path';
      if (id === 'variant-type') variantPosition = 'path';
    } else {
      forQuery.push(filter);
    }
  });

  //model path value (model name) can not make a valid model id without make data
  if (modelPosition === 'path' && makePosition !== 'path') {
    const modelFilterIndex = forPath.findIndex(fltr => fltr.id === 'model');
    const removedModel = forPath.splice(modelFilterIndex, 1);

    if (makePosition === 'query') {
      forQuery.push(removedModel[0]);
    }
  }

  //variant path value (variant name) can not make a valid variant id without make and model data
  if (variantPosition === 'path' && (makePosition !== 'path' || modelPosition !== 'path')) {
    const trimFilterIndex = forPath.findIndex(fltr => fltr.id === 'variant-type');
    const removedVariant = forPath.splice(trimFilterIndex, 1);

    if (makePosition !== undefined && modelPosition !== undefined) {
      forQuery.push(removedVariant[0]);
    }
  }

  //range filters are grouped and sent to path or query together
  const rangeFltrsProcessed: string[] = [];
  rangeFltrs.forEach(rangeFltr => {
    const fltrBaseName = rangeFltr.id.split('__')[0];
    const isSpaceInPath = hasSpaceInPath(
      { addition: rangeFltr, pathList: forPath, allFilters: paramsArray },
      root,
    );

    if (!rangeFltrsProcessed.includes(fltrBaseName)) {
      const partnerFltr = rangeFltrs.find(
        fltr => fltr.id === getRangeFilterPartnerId(rangeFltr.id),
      );

      //only price/monthly-price lte on its own has a slug param
      if (
        considerPriceForPath(fltrBaseName, root) &&
        hasPriceRangeForPath(rangeFltr, partnerFltr) &&
        isSpaceInPath
      ) {
        const lteFilter = rangeFltr.id.endsWith('__lte') ? rangeFltr : partnerFltr;
        if (lteFilter) forPath.push(lteFilter);
      }
      //engine size only goes to slug if it matches
      else if (
        fltrBaseName === 'engine-size' &&
        rangeFltr.value === partnerFltr?.value &&
        isSpaceInPath
      ) {
        const groupedFltr = {
          ...rangeFltr,
          value: getRangeVal(rangeFltr, partnerFltr),
        };
        forPath.push(groupedFltr);
      } else {
        forQuery.push(rangeFltr);
        if (partnerFltr) forQuery.push(partnerFltr);
      }

      rangeFltrsProcessed.push(fltrBaseName);
    }
  });

  //order & build path
  SLUG_PARAM_KEYS.forEach(slugParam => {
    const filterKeys = slugParamMapping[slugParam](root);
    const filter = forPath.find(fltr => fltr.id === filterKeys[0]);
    if (filter) {
      newPath.push(`/${getSlugParamValueFromFilter(filter)}`);
    }
  });

  //build query
  const queryString = createQueryStrFromFilters(forQuery);

  return `${newPath.join('')}${queryString ? `?${queryString}` : ''}`;
}

export { createPathFromFilters };
